// src/services/featureService.ts - Comprehensive feature gating system
import { Subscription, SubscriptionAddon } from '../types/subscription';
import { User } from '../types/user';

export type PlanType = 'essential' | 'gold' | 'premium';

export type FeatureKey =
  // Essential features
  | 'bcp_generation'
  | 'incident_response_checklists'
  | 'basic_support'
  | 'document_print'
  | 'document_download'
  | 'ai_analysis_print'

  // Gold features (includes all Essential)
  | 'flash_rtc'
  | 'dedicated_onboarding'

  // Premium features (includes all Gold + Essential)
  | 'executive_training'
  | 'iso_27001_readiness'
  | 'tabletop_scenarios'
  | 'insurer_reports'
  | 'resilience_badge'
  | 'priority_support'
  | 'quarterly_checkins';

export class FeatureService {
  private static planHierarchy: Record<PlanType, number> = {
    essential: 1,
    gold: 2,
    premium: 3
  };

  private static featureRequirements: Record<FeatureKey, PlanType> = {
    // Essential features
    bcp_generation: 'essential',
    incident_response_checklists: 'essential',
    basic_support: 'essential',
    document_print: 'essential',
    document_download: 'essential',
    ai_analysis_print: 'essential',

    // Gold features
    flash_rtc: 'gold',
    dedicated_onboarding: 'gold',

    // Premium features
    executive_training: 'premium',
    iso_27001_readiness: 'premium',
    tabletop_scenarios: 'premium',
    insurer_reports: 'premium',
    resilience_badge: 'premium',
    priority_support: 'premium',
    quarterly_checkins: 'premium'
  };

  // Mapping of addon types to features they unlock
  private static addonFeatures: Record<string, FeatureKey[]> = {
    tabletop_creator: ['tabletop_scenarios', 'insurer_reports'],
    iso27001_readiness: ['iso_27001_readiness'],
    executive_training: ['executive_training'],
    flash_rtc: ['flash_rtc'],
    insurer_reports: ['insurer_reports'],
    resilience_badge: ['resilience_badge'],
  };

  static canAccessFeature(
    subscription: Subscription | null,
    feature: FeatureKey,
    user?: User | null,
    addons?: SubscriptionAddon[]
  ): boolean {
    // Admin users can access all features regardless of subscription
    if (user && (user.is_superuser || user.role === 'ADMIN' || user.role === 'SUPER_ADMIN')) {
      return true;
    }

    if (!subscription || subscription.status !== 'active') {
      return false;
    }

    // Check base plan access first (existing logic)
    const userPlanLevel = this.planHierarchy[subscription.plan_type];
    const requiredPlanLevel = this.planHierarchy[this.featureRequirements[feature]];

    if (userPlanLevel >= requiredPlanLevel) {
      return true;
    }

    // Check addon access (new logic - backward compatible)
    const activeAddons = addons || subscription.addons || [];
    for (const addon of activeAddons) {
      if (addon.status === 'active') {
        const addonFeatures = this.addonFeatures[addon.addon_type] || [];
        if (addonFeatures.includes(feature)) {
          return true;
        }
      }
    }

    return false;
  }

  static getRequiredAddonForFeature(feature: FeatureKey): string | null {
    // Check which addon unlocks this feature
    for (const [addonType, features] of Object.entries(this.addonFeatures)) {
      if (features.includes(feature)) {
        return addonType;
      }
    }
    return null;
  }

  static getRequiredPlan(feature: FeatureKey): PlanType {
    return this.featureRequirements[feature];
  }

  static getPlanFeatures(planType: PlanType): FeatureKey[] {
    const planLevel = this.planHierarchy[planType];
    return Object.entries(this.featureRequirements)
      .filter(([_, requiredPlan]) => this.planHierarchy[requiredPlan] <= planLevel)
      .map(([feature]) => feature as FeatureKey);
  }

  static getMissingFeatures(subscription: Subscription | null, targetPlan: PlanType): FeatureKey[] {
    if (!subscription) {
      return this.getPlanFeatures(targetPlan);
    }

    const currentLevel = this.planHierarchy[subscription.plan_type];
    const targetLevel = this.planHierarchy[targetPlan];

    if (currentLevel >= targetLevel) {
      return [];
    }

    return Object.entries(this.featureRequirements)
      .filter(([_, requiredPlan]) => {
        const requiredLevel = this.planHierarchy[requiredPlan];
        return requiredLevel > currentLevel && requiredLevel <= targetLevel;
      })
      .map(([feature]) => feature as FeatureKey);
  }

  static getFeatureDescription(feature: FeatureKey): string {
    const descriptions: Record<FeatureKey, string> = {
      // Essential features
      bcp_generation: 'BCP generation aligned with ISO 22301:2019',
      incident_response_checklists: 'Incident response checklists & SOPs',
      basic_support: 'Basic customer support',
      document_print: 'Print BCP documents',
      document_download: 'Download BCP documents',
      ai_analysis_print: 'Print AI analysis results',

      // Gold features
      flash_rtc: 'Flash RTC out-of-band communication',
      dedicated_onboarding: 'Dedicated onboarding support',

      // Premium features
      executive_training: 'Executive leadership training modules',
      iso_27001_readiness: 'ISO 27001 readiness (cybersecurity)',
      tabletop_scenarios: 'Customized tabletop scenarios',
      insurer_reports: 'Insurer reports',
      resilience_badge: 'Resilience Badge (up to 10% insurance discount)',
      priority_support: 'Priority support',
      quarterly_checkins: 'Quarterly check-ins'
    };

    return descriptions[feature] || feature;
  }
}

export default FeatureService;
